import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Search, X } from 'lucide-react';
import { NODE_CATALOG, paletteGrouped } from 'features/agents/WorkflowEditor/nodeCatalog';
import NodeMark from 'features/agents/WorkflowEditor/NodeMark';
import { APP_LOGOS, LOGO_NEEDS_ADAPTIVE } from 'features/agents/WorkflowEditor/appLogos';

/**
 * "Choose a trigger" / "Add a step".
 *
 * A search box over sections of compact rows: a 24px tile, the name, and what
 * it does in one muted line. While the agent has no trigger only triggers are
 * offered; after that, Flow, Data and AI, then one collapsible group per app
 * (Slack, GitHub, Linear). Recently added kinds come first.
 *
 * Keyboard: focus starts in the search box; Up/Down move, Enter picks (or
 * opens an app), Left/Right collapse and expand an app, Escape closes.
 *
 * Colour is carried by nothing here: the tiles are NodeMark's neutral tile,
 * and only a trigger's tile is brand-soft.
 */

const RECENT_KEY = 'trodo.agents.recentNodes';
const RECENT_MAX = 5;
const BRAND = { slack: 'Slack', github: 'GitHub', linear: 'Linear' };

function readRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((k) => typeof k === 'string') : [];
  } catch (_) {
    return [];
  }
}

function writeRecent(kind) {
  try {
    const next = [kind, ...readRecents().filter((k) => k !== kind)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* private mode */ }
}

/** "Slack: Send message" shows as "Send message" under a Slack heading. */
export function shortName(item, provider) {
  const brand = BRAND[provider || item.provider || item.triggerProvider];
  return brand ? item.displayName.replace(new RegExp(`^${brand}:\\s*`), '') : item.displayName;
}

/**
 * The rows for the current state, as one flat list. `selectable` entries
 * (apps and nodes) take part in keyboard navigation; headers do not.
 */
export function buildEntries({ groups, onlyTriggers, query, expanded, recents }) {
  const byId = Object.fromEntries(groups.map((g) => [g.id, g]));
  const out = [];
  const header = (id, label) => out.push({ type: 'header', id: `h-${id}`, label });
  const node = (item, provider, extra = {}) => out.push({ type: 'node', id: `n-${item.kind}`, item, provider, ...extra });

  const q = query.trim().toLowerCase();
  const hit = (item, provider) => {
    const hay = `${item.displayName} ${item.description || ''} ${BRAND[provider] || ''}`.toLowerCase();
    return q.split(/\s+/).every((w) => hay.includes(w));
  };

  const tg = byId.triggers;
  const appGroups = onlyTriggers ? (tg?.providerGroups || []) : (byId.actions?.providerGroups || []);

  if (q) {
    const plain = onlyTriggers
      ? (tg?.items || [])
      : ['flow', 'data', 'ai'].flatMap((id) => byId[id]?.items || []);
    const matched = plain.filter((i) => hit(i));
    if (matched.length) {
      header('results', onlyTriggers ? 'Triggers' : 'Steps');
      matched.forEach((i) => node(i, null));
    }
    for (const pg of appGroups) {
      const items = pg.items.filter((i) => hit(i, pg.provider));
      if (!items.length) continue;
      header(`app-${pg.provider}`, onlyTriggers ? `${pg.label} events` : pg.label);
      items.forEach((i) => node(i, pg.provider, { showBrand: false }));
    }
    return out;
  }

  const known = new Set(Object.keys(NODE_CATALOG));
  const recent = recents.filter((k) => known.has(k) && ((NODE_CATALOG[k].category === 'trigger') === onlyTriggers));
  if (recent.length) {
    header('recent', 'Recent');
    recent.forEach((k) => node(NODE_CATALOG[k], NODE_CATALOG[k].provider || NODE_CATALOG[k].triggerProvider || null, { id: `r-${k}` }));
  }

  const app = (pg) => {
    const open = expanded.has(pg.provider);
    out.push({ type: 'app', id: `a-${pg.provider}`, provider: pg.provider, label: onlyTriggers ? `${pg.label} events` : pg.label, count: pg.items.length, open });
    if (!open) return;
    const folders = onlyTriggers ? null : pg.actionGroups;
    if (folders?.length) {
      folders.forEach((ag) => {
        out.push({ type: 'subheader', id: `s-${pg.provider}-${ag.id}`, label: ag.label });
        ag.items.forEach((i) => node(i, pg.provider, { indent: true }));
      });
    } else {
      pg.items.forEach((i) => node(i, pg.provider, { indent: true }));
    }
  };

  if (onlyTriggers) {
    header('triggers', 'Triggers');
    (tg?.items || []).forEach((i) => node(i, null));
    appGroups.forEach(app);
    return out;
  }
  for (const id of ['flow', 'data', 'ai']) {
    const g = byId[id];
    if (!g?.items?.length) continue;
    header(id, g.label);
    g.items.forEach((i) => node(i, null));
  }
  if (appGroups.length) {
    header('apps', 'Apps');
    appGroups.forEach(app);
  }
  return out;
}

function AppMark({ provider }) {
  const logo = APP_LOGOS[provider];
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)]">
      {logo ? <img src={logo} alt="" className={`h-3.5 w-3.5 object-contain${LOGO_NEEDS_ADAPTIVE.has(logo) ? ' node-icon-adaptive' : ''}`} /> : null}
    </span>
  );
}

const rowBase = 'flex h-11 w-full items-center gap-2.5 px-3 text-left';

export default function NodePicker({ onClose, onPick, hasTrigger, anchored }) {
  const onlyTriggers = !hasTrigger && !anchored;
  const groups = useMemo(() => paletteGrouped(), []);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [recents] = useState(readRecents);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const entries = useMemo(
    () => buildEntries({ groups, onlyTriggers, query, expanded, recents }),
    [groups, onlyTriggers, query, expanded, recents],
  );
  const selectable = useMemo(() => entries.filter((e) => e.type === 'node' || e.type === 'app'), [entries]);
  const current = selectable[Math.min(active, selectable.length - 1)] || null;

  useEffect(() => { setActive(0); }, [query]);

  useEffect(() => {
    if (!current || !listRef.current) return;
    const el = Array.from(listRef.current.querySelectorAll('[data-entry]')).find((n) => n.dataset.entry === current.id);
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const toggleApp = useCallback((provider, force) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const open = force === undefined ? !next.has(provider) : force;
      if (open) next.add(provider); else next.delete(provider);
      return next;
    });
  }, []);

  const choose = useCallback((entry) => {
    if (!entry) return;
    if (entry.type === 'app') { toggleApp(entry.provider); return; }
    writeRecent(entry.item.kind);
    onPick(entry.item.kind);
  }, [onPick, toggleApp]);

  const onKeyDown = (e) => {
    if (!selectable.length) return;
    const idx = Math.min(active, selectable.length - 1);
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((idx + 1) % selectable.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((idx - 1 + selectable.length) % selectable.length); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(selectable.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(current); }
    else if (e.key === 'ArrowRight' && current?.type === 'app' && !query) { e.preventDefault(); toggleApp(current.provider, true); }
    else if (e.key === 'ArrowLeft' && !query) {
      if (current?.type === 'app') { e.preventDefault(); toggleApp(current.provider, false); }
      else if (current?.provider && current.indent) {
        // From a row inside an app, Left folds the app and lands on its heading.
        e.preventDefault();
        toggleApp(current.provider, false);
        const appIdx = selectable.findIndex((s) => s.type === 'app' && s.provider === current.provider);
        if (appIdx >= 0) setActive(appIdx);
      }
    }
  };

  // Escape closes from anywhere in the page, not only from the search box.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title = onlyTriggers ? 'Choose a trigger' : 'Add a step';
  const activeDomId = current ? `np-${current.id}` : undefined;

  return (
    <div
      className="fixed inset-0 z-[55]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 25%, transparent)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="absolute left-1/2 top-[10vh] flex max-h-[min(560px,80vh)] w-[min(480px,calc(100vw-24px))] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex h-11 shrink-0 items-center justify-between pl-3 pr-1.5">
          <h2 className="text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="shrink-0 px-3 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[color:var(--text-muted)]" aria-hidden />
            <input
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls="np-list"
              aria-activedescendant={activeDomId}
              aria-label={onlyTriggers ? 'Search triggers' : 'Search steps'}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={onlyTriggers ? 'Search triggers' : 'Search steps'}
              className="h-8 w-full rounded-md border border-[color:var(--border-default)] bg-transparent pl-8 pr-2 text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] focus:border-[color:var(--border-strong)] focus:outline-none"
            />
          </div>
        </div>

        <div id="np-list" ref={listRef} role="listbox" aria-label={title} className="min-h-0 flex-1 overflow-y-auto border-t border-[color:var(--border-subtle)] pb-1">
          {selectable.length === 0 ? (
            <p className="px-3 py-8 text-center text-[12px] text-[color:var(--text-muted)]">Nothing matches &ldquo;{query.trim()}&rdquo;.</p>
          ) : entries.map((e) => {
            if (e.type === 'header') {
              return <div key={e.id} role="presentation" className="px-3 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{e.label}</div>;
            }
            if (e.type === 'subheader') {
              return <div key={e.id} role="presentation" className="px-3 pb-0.5 pt-2 pl-[3.25rem] text-[11px] text-[color:var(--text-muted)]">{e.label}</div>;
            }
            const isActive = current?.id === e.id;
            const common = {
              id: `np-${e.id}`,
              'data-entry': e.id,
              role: 'option',
              'aria-selected': isActive,
              onMouseMove: () => { const i = selectable.findIndex((s) => s.id === e.id); if (i >= 0 && i !== active) setActive(i); },
              onClick: () => choose(e),
            };
            const rowCls = `${rowBase} ${isActive ? 'bg-[color:var(--surface-hover)]' : ''}`;
            if (e.type === 'app') {
              return (
                <div key={e.id} {...common} aria-expanded={e.open} className={`${rowCls} cursor-pointer`}>
                  <AppMark provider={e.provider} />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[color:var(--text-primary)]">{e.label}</span>
                  <span className="text-[11px] text-[color:var(--text-muted)]">{e.count}</span>
                  <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)] transition-transform duration-150 ${e.open ? 'rotate-90' : ''}`} aria-hidden />
                </div>
              );
            }
            const spec = e.item;
            return (
              <div key={e.id} {...common} className={`${rowCls} cursor-pointer ${e.indent ? 'pl-[3.25rem]' : ''}`}>
                {e.indent ? null : <NodeMark kind={spec.kind} size={24} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] leading-[17px] text-[color:var(--text-primary)]">{shortName(spec, e.provider)}</span>
                  {spec.description ? <span className="block truncate text-[12px] leading-[16px] text-[color:var(--text-muted)]">{spec.description}</span> : null}
                </span>
                {query && e.provider && e.showBrand !== false ? <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">{BRAND[e.provider]}</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
